// src/components/Layout/Layout.js
import React, { useState, useEffect, useCallback } from 'react';
import Logo from '../../assets/kohara_bg.png';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
  Badge,
} from '@mui/material';
import { UserAvatar } from '../../shared/components/common';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LogoutIcon from '@mui/icons-material/Logout';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import { getDoc, doc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../../firebase';

import { useAuth } from '../../context/AuthContext';
import { useFeatures } from '../../context/FeatureContext';
import { useNotificationCounts } from '../../hooks/useNotificationCounts';
import { prefetchService, cacheService, CACHE_TTL } from '../../services';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  background: 'linear-gradient(to right, #30697D, #0D2B35)',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const DraggableListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isReorderMode',
})(({ theme, isDragging, isReorderMode }) => ({
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.25, 0.5),
  backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
  boxShadow: isDragging ? theme.shadows[4] : 'none',
  transform: isDragging ? 'rotate(2deg)' : 'none',
  '&:hover': {
    backgroundColor: isReorderMode ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
  },
}));

// Base menu items without delivery-dependent features
const baseMenuItems = [
  { 
    id: 'dashboard',
    label: 'Dashboard', 
    icon: <DashboardIcon />, 
    path: '/' 
  },
  { 
    id: 'users',
    label: 'Benutzerverwaltung', 
    icon: <PeopleIcon />, 
    path: '/users' 
  },
  { 
    id: 'time-tracking',
    label: 'Zeiterfassung', 
    icon: <AccessTimeIcon />, 
    path: '/time-tracking' 
  },
  { 
    id: 'calendar',
    label: 'Kalender', 
    icon: <EventNoteIcon />, 
    path: '/calendar' 
  },
  {
    id: 'absences',
    label: 'Abwesenheiten',
    icon: <HolidayVillageIcon />,
    path: '/absences'
  },
  {
    id: 'todos',
    label: 'Aufgaben', 
    icon: <AssignmentIcon />, 
    path: '/todos' 
  },
  { 
    id: 'spesen',
    label: 'Spesen', 
    icon: <ReceiptLongIcon />, 
    path: '/spesen' 
  }
];

// Delivery-dependent menu items
const deliveryMenuItems = [
  {
    id: 'vehicles',
    label: 'Fahrzeuge & Checks',
    icon: <LocalShippingIcon />,
    path: '/vehicles'
  },
  {
    id: 'deliveries',
    label: 'Lieferscheine',
    icon: <ReceiptIcon />,
    path: '/deliveries'
  }
];

/**
 * Navigation prefetch configuration
 * Maps routes to their data prefetch functions
 */
const NAV_PREFETCH_CONFIG = {
  '/absences': {
    key: 'nav-absences-list',
    ttl: CACHE_TTL.ABSENCES,
    queryFn: async (companyIds) => {
      if (!companyIds?.length) return [];
      const q = query(
        collection(db, 'absences'),
        where('companyId', 'in', companyIds.slice(0, 10)),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }
  },
  '/time-tracking': {
    key: 'nav-time-tracking-users',
    ttl: CACHE_TTL.USER_PROFILES,
    queryFn: async (companyIds) => {
      if (!companyIds?.length) return [];
      const q = query(
        collection(db, 'users'),
        where('companyId', 'in', companyIds.slice(0, 10))
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }
  },
  '/vehicles': {
    key: 'nav-vehicles-list',
    ttl: CACHE_TTL.VEHICLES,
    queryFn: async (companyIds) => {
      if (!companyIds?.length) return [];
      // Vehicles are stored in company subcollections
      const allVehicles = [];
      for (const companyId of companyIds) {
        try {
          const q = query(collection(db, 'companies', companyId, 'vehicles'));
          const snapshot = await getDocs(q);
          snapshot.docs.forEach(d => {
            allVehicles.push({ id: d.id, companyId, ...d.data() });
          });
        } catch (err) {
          // Error handled silently
        }
      }
      return allVehicles;
    }
  },
  '/todos': {
    key: 'nav-todos-active',
    ttl: CACHE_TTL.TODOS,
    queryFn: async (companyIds) => {
      if (!companyIds?.length) return [];
      const q = query(
        collection(db, 'todos'),
        where('companyId', 'in', companyIds.slice(0, 10)),
        where('status', '==', 'active'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }
  },
  '/spesen': {
    key: 'nav-expenses-list',
    ttl: CACHE_TTL.EXPENSES,
    queryFn: async (companyIds) => {
      if (!companyIds?.length) return [];
      // Expenses are stored in company subcollections
      const allExpenses = [];
      for (const companyId of companyIds) {
        try {
          const q = query(
            collection(db, 'companies', companyId, 'spesen'),
            limit(30)
          );
          const snapshot = await getDocs(q);
          snapshot.docs.forEach(d => {
            allExpenses.push({ id: d.id, companyId, ...d.data() });
          });
        } catch (err) {
          // Error handled silently
        }
      }
      return allExpenses;
    }
  },
  '/users': {
    key: 'nav-users-list',
    ttl: CACHE_TTL.USER_PROFILES,
    queryFn: async (companyIds) => {
      if (!companyIds?.length) return [];
      const q = query(
        collection(db, 'users'),
        where('companyId', 'in', companyIds.slice(0, 10))
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }
  }
};

export default function Layout() {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const { features, loading: featuresLoading } = useFeatures();
  const { counts: notificationCounts } = useNotificationCounts(user);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Draggable navigation states
  const [menuItems, setMenuItems] = useState([]);
  const [isReorderMode, setIsReorderMode] = useState(false);

  // Get the storage key for menu order
  const getMenuOrderKey = useCallback(() => {
    return `menuOrder_${user?.id || 'default'}`;
  }, [user?.id]);

  // Get all available menu items based on features
  const getAllMenuItems = useCallback(() => {
    let items = [...baseMenuItems];
    if (features.delivery) {
      items = [...items, ...deliveryMenuItems];
    }
    return items;
  }, [features.delivery]);

  // Load saved menu order from localStorage
  const loadMenuOrder = useCallback(() => {
    const allItems = getAllMenuItems();
    const savedOrder = localStorage.getItem(getMenuOrderKey());
    
    if (savedOrder && user?.id) {
      try {
        const orderData = JSON.parse(savedOrder);
        
        // Validate that saved order contains valid menu items
        if (Array.isArray(orderData) && orderData.length > 0) {
          const orderedItems = [];
          
          // First, add items in saved order
          orderData.forEach(savedItem => {
            if (savedItem && savedItem.id) {
              const foundItem = allItems.find(item => item.id === savedItem.id);
              if (foundItem) {
                orderedItems.push(foundItem);
              }
            }
          });
          
          // Then, add any new items that weren't in the saved order
          allItems.forEach(item => {
            if (!orderedItems.find(orderedItem => orderedItem.id === item.id)) {
              orderedItems.push(item);
            }
          });
          
          setMenuItems(orderedItems);
          return;
        }
      } catch (error) {
        // Error handled silently
      }
    }
    
    // Fallback to default order
    setMenuItems(allItems);
  }, [getAllMenuItems, getMenuOrderKey, user?.id]);

  // Save menu order to localStorage
  const saveMenuOrder = useCallback((items) => {
    if (user?.id && Array.isArray(items)) {
      try {
        const orderData = items.map(item => ({
          id: item.id,
          label: item.label,
          path: item.path
        }));
        localStorage.setItem(getMenuOrderKey(), JSON.stringify(orderData));
      } catch (error) {
        // Error handled silently
      }
    }
  }, [getMenuOrderKey, user?.id]);

  // Handle drag end event
  const handleDragEnd = useCallback((result) => {
    // Ignore if dropped outside droppable area
    if (!result.destination) {
      return;
    }

    // Ignore if dropped in same position
    if (result.source.index === result.destination.index) {
      return;
    }

    const newItems = Array.from(menuItems);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    setMenuItems(newItems);
    saveMenuOrder(newItems);
  }, [menuItems, saveMenuOrder]);

  // Toggle reorder mode
  const toggleReorderMode = useCallback(() => {
    setIsReorderMode(prev => !prev);
  }, []);

  // Reset menu order to default
  const resetMenuOrder = useCallback(() => {
    const defaultOrder = getAllMenuItems();
    setMenuItems(defaultOrder);
    saveMenuOrder(defaultOrder);
    setIsReorderMode(false);
  }, [getAllMenuItems, saveMenuOrder]);

  // Helper function to get notification count for menu item
  const getNotificationCount = (path) => {
    switch (path) {
      case '/absences':
        return notificationCounts.pendingAbsences;
      case '/spesen':
        return notificationCounts.pendingSpesen;
      default:
        return 0;
    }
  };

  /**
   * Hover prefetch handlers for navigation items
   * Prefetch data when user hovers over a nav link (with debounce)
   * NOTE: Increased debounce from 150ms to 500ms to reduce accidental prefetches
   */
  const handleNavMouseEnter = useCallback((path) => {
    const config = NAV_PREFETCH_CONFIG[path];
    if (config && user?.adminCompanyIds?.length) {
      const companyIds = user.adminCompanyIds;
      prefetchService.prefetchOnHover(
        config.key,
        () => config.queryFn(companyIds),
        500, // 500ms debounce (increased from 150ms to reduce accidental prefetches)
        { ttl: config.ttl }
      );
    }
  }, [user?.adminCompanyIds]);

  const handleNavMouseLeave = useCallback((path) => {
    const config = NAV_PREFETCH_CONFIG[path];
    if (config) {
      prefetchService.cancelHoverPrefetch(config.key);
    }
  }, []);

  // Load menu order on component mount and when dependencies change
  useEffect(() => {
    loadMenuOrder();
  }, [loadMenuOrder]);
  
  useEffect(() => {
    // If we already have a companyName in the User object, use that
    if (user && user.companyName) {
      setCompanyName(user.companyName);
      return;
    }
    
    // Otherwise, try to load the company name from Firestore
    const fetchCompanyData = async () => {
      if (user && user.companyId) {
        setLoading(true);
        try {
          const companyDoc = await getDoc(doc(db, 'companies', user.companyId));
          if (companyDoc.exists()) {
            const name = companyDoc.data().name || 'Unbekannte Firma';
            setCompanyName(name);

            // Update the user in context only (no localStorage - security improvement)
            setUser({ companyName: name });
          }
        } catch (error) {
          // Error handled silently
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCompanyData();
  }, [user, setUser]);

  // Handle navigation restrictions for delivery features
  useEffect(() => {
    if (!featuresLoading && !features.delivery) {
      if (location.pathname === '/vehicles' ||
          location.pathname.startsWith('/vehicles/') ||
          location.pathname === '/deliveries') {
        navigate('/');
      }
    }
  }, [location.pathname, features.delivery, featuresLoading, navigate]);

  /**
   * Predictive prefetch based on current page
   * DISABLED: This was causing excessive Firestore reads (~50 reads per page load)
   * The prefetch would fire on every navigation, even when data was already cached
   *
   * If re-enabling, ensure:
   * 1. Cache is checked BEFORE triggering prefetch
   * 2. Debounce is increased significantly
   * 3. Only prefetch if user stays on page for >5 seconds
   */
  // useEffect(() => {
  //   if (!user?.adminCompanyIds?.length) return;
  //   const companyIds = user.adminCompanyIds;
  //   const PREDICTIVE_PREFETCH = {
  //     '/': ['/absences', '/time-tracking'],
  //     '/absences': ['/users'],
  //     '/time-tracking': ['/users'],
  //     '/users': ['/time-tracking', '/absences'],
  //   };
  //   const currentPath = location.pathname;
  //   const nextPaths = PREDICTIVE_PREFETCH[currentPath];
  //   if (nextPaths) {
  //     const timer = setTimeout(() => {
  //       nextPaths.forEach(path => {
  //         const config = NAV_PREFETCH_CONFIG[path];
  //         if (config) {
  //           prefetchService.prefetch(
  //             config.key,
  //             () => config.queryFn(companyIds),
  //             { ttl: config.ttl }
  //           );
  //         }
  //       });
  //     }, 2000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [location.pathname, user?.adminCompanyIds]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Clear prefetch state and cache on logout
      prefetchService.clear();
      cacheService.clear();
      // Note: AuthContext's onAuthStateChanged will clear user state automatically
      navigate('/login');
    } catch (error) {
      prefetchService.clear();
      cacheService.clear();
      navigate('/login');
    } finally {
      handleMenuClose();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <StyledAppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.role || '....'}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <UserAvatar user={user} size={32} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            keepMounted
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </StyledAppBar>
      
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#fafafa',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader sx={{ flexDirection: 'column', alignItems: 'flex-start', pb: 2 }}>
          <Box sx={{ bgcolor: '#30697D', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <img 
                src={Logo} 
                alt="kohara Web Logo" 
                style={{ 
                  height: '56px', 
                  width: 'auto', 
                  marginRight: '12px', 
                  marginTop: '6px',
                  marginBottom: '6px'
                }} 
              />
              <Typography variant="h6" sx={{ fontWeight: 400, color: 'white' }}>
                kohara
              </Typography>
            </Box>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Typography 
              variant="subtitle1" 
              sx={{ 
                ml: 2, 
                mt: 1, 
                fontWeight: 'medium',
                color: 'primary.main' 
              }}
            >
              {companyName}
            </Typography>
          )}
        </DrawerHeader>
        
        <Divider />
        
        {/* Menu Customization Controls */}
        <Box sx={{ px: 1, py: 1 }}>
          <Tooltip title={isReorderMode ? "Sortierung beenden" : "Menüpunkte neu anordnen"}>
            <ListItemButton 
              onClick={toggleReorderMode}
              sx={{ 
                borderRadius: 1,
                mb: 0.5,
                backgroundColor: isReorderMode ? '#30697D' : 'transparent',
                color: isReorderMode ? 'white' : 'inherit',
                '&:hover': {
                  backgroundColor: isReorderMode ? '#285a6b' : 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                {isReorderMode ? <CheckIcon /> : <TuneIcon />}
              </ListItemIcon>
              <ListItemText 
                primary={isReorderMode ? "Fertig" : "Anordnen"} 
                primaryTypographyProps={{ 
                  fontWeight: isReorderMode ? 600 : 400,
                  fontSize: '0.9rem'
                }}
              />
            </ListItemButton>
          </Tooltip>
          
          {isReorderMode && (
            <ListItemButton 
              onClick={resetMenuOrder}
              sx={{ 
                borderRadius: 1,
                fontSize: '0.85rem',
                color: 'text.secondary'
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <DragIndicatorIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Zurücksetzen" 
                primaryTypographyProps={{ 
                  fontSize: '0.85rem'
                }}
              />
            </ListItemButton>
          )}
        </Box>
        
        <Divider />
        
        {/* Navigation Menu */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="menu-items">
              {(provided, snapshot) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{
                    py: 1,
                    backgroundColor: snapshot.isDraggingOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {menuItems.map((item, index) => (
                    <Draggable 
                      key={item.id} 
                      draggableId={item.id} 
                      index={index}
                      isDragDisabled={!isReorderMode}
                    >
                      {(provided, snapshot) => (
                        <DraggableListItem
                          disablePadding
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          isDragging={snapshot.isDragging}
                          isReorderMode={isReorderMode}
                          style={{
                            ...provided.draggableProps.style,
                          }}
                        >
                          <ListItemButton
                            onClick={() => !isReorderMode && navigate(item.path)}
                            onMouseEnter={() => !isReorderMode && handleNavMouseEnter(item.path)}
                            onMouseLeave={() => !isReorderMode && handleNavMouseLeave(item.path)}
                            selected={!isReorderMode && location.pathname === item.path}
                            sx={{
                              borderRadius: 1,
                              cursor: isReorderMode ? 'default' : 'pointer',
                              pointerEvents: 'auto',
                              '&.Mui-selected': {
                                backgroundColor: '#30697D',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#285a6b',
                                },
                                '& .MuiListItemIcon-root': {
                                  color: 'white',
                                }
                              },
                              '&:hover': {
                                backgroundColor: isReorderMode ? 'transparent' : undefined,
                              }
                            }}
                          >
                            {isReorderMode && (
                              <Box
                                {...provided.dragHandleProps}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mr: 1,
                                  color: 'primary.main',
                                  cursor: 'grab',
                                  padding: '4px',
                                  borderRadius: '4px',
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                    color: 'primary.dark',
                                  },
                                  '&:active': { 
                                    cursor: 'grabbing',
                                    backgroundColor: 'rgba(25, 118, 210, 0.3)',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DragIndicatorIcon fontSize="medium" />
                              </Box>
                            )}
                            <ListItemIcon sx={{ 
                              minWidth: isReorderMode ? 35 : 56,
                              transition: 'min-width 0.2s ease'
                            }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={item.label} 
                              primaryTypographyProps={{
                                fontWeight: (!isReorderMode && location.pathname === item.path) ? 600 : 400,
                                color: isReorderMode ? 'text.secondary' : 'inherit'
                              }}
                            />
                            {/* Show badge for items with pending notifications */}
                            {(() => {
                              const count = getNotificationCount(item.path);
                              return count > 0 && !isReorderMode ? (
                                <Badge 
                                  badgeContent={count} 
                                  color="error" 
                                  sx={{ 
                                    mr: 2,
                                    ml: -1,
                                    '& .MuiBadge-badge': { 
                                      fontSize: '0.75rem',
                                      minWidth: '20px',
                                      height: '20px'
                                    } 
                                  }}
                                />
                              ) : null;
                            })()}
                          </ListItemButton>
                        </DraggableListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Drawer>
      
      <Main open={open}>
        <DrawerHeader />
        <Outlet />
      </Main>
    </Box>
  );
}